import React, { useState } from "react";
import { ArrowRight, Building2, MapPin, Mail, Shield } from "lucide-react";
import { formatGain } from "../data/questions";

const ContactForm = ({ score, gainMinutes, onSubmit, submitting, error }) => {
  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    company: "",
    postal_code: "",
    email: "",
    rgpd_consent: false,
  });
  const [errors, setErrors] = useState({});

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstname.trim()) e.firstname = "Champ obligatoire";
    if (!form.lastname.trim()) e.lastname = "Champ obligatoire";
    if (!form.company.trim()) e.company = "Champ obligatoire";
    if (!/^\d{5}$/.test(form.postal_code.trim()))
      e.postal_code = "Code postal invalide (5 chiffres)";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "Email invalide";
    if (!form.rgpd_consent)
      e.rgpd_consent = "Le consentement est obligatoire";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (validate()) onSubmit(form);
  };

  const inputStyle = (field) => ({
    width: "100%",
    padding: "12px 14px",
    backgroundColor: "#F9FAFB",
    border: `1.5px solid ${errors[field] ? "#C62828" : "#E5E7EB"}`,
    borderRadius: 10,
    fontSize: 14,
    fontFamily: "Inter, sans-serif",
    color: "#12354D",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  });

  const iconInputStyle = (field) => ({
    ...inputStyle(field),
    paddingLeft: 38,
  });

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 6,
    fontFamily: "Inter, sans-serif",
  };

  const errStyle = {
    color: "#C62828",
    fontSize: 11,
    marginTop: 4,
    fontFamily: "Inter, sans-serif",
  };

  return (
    <div className="screen-container" style={{ paddingTop: 20 }}>
      <div className="content-wrapper">
        {/* Header */}
        <div className="text-center mb-5">
          <img
            src="/logos._lbs_conecti.png"
            alt="Groupe LBS Conecti"
            style={{
              display: "block",
              margin: "0 auto",
              width: "100%",
              height: "auto",
            }}
          />
        </div>

        {/* Score recap */}
        <div
          className="flex items-center gap-4 rounded-xl p-4 mb-5"
          style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}
        >
          <div>
            <p style={{ color: "rgba(107, 114, 128, 1)", fontSize: 11, fontFamily: "Inter, sans-serif" }}>Score</p>
            <p style={{ color: "rgba(107, 114, 128, 1)", fontWeight: 700, fontSize: 22, fontFamily: "Montserrat, sans-serif" }}>
              {score}/100
            </p>
          </div>
          <div style={{ width: 1, height: 40, backgroundColor: "rgba(255,255,255,0.2)" }} />
          <div>
            <p style={{ color: "rgba(107, 114, 128, 1)", fontSize: 11, fontFamily: "Inter, sans-serif" }}>Gain potentiel</p>
            <p
              style={{
                fontWeight: 700,
                fontSize: 22,
                fontFamily: "Montserrat, sans-serif",
                color: gainMinutes > 90 ? "#F87171" : "green",
              }}
            >
              {formatGain(gainMinutes)} / jour
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="card p-6">
          <form onSubmit={handleSubmit}>
            {/* Name row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <label style={labelStyle}>Prénom *</label>
                <input
                  data-testid="input-firstname"
                  type="text"
                  value={form.firstname}
                  onChange={(e) => set("firstname", e.target.value)}
                  placeholder="Marie"
                  style={inputStyle("firstname")}
                  onFocus={(e) => (e.target.style.borderColor = "#12354D")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = errors.firstname ? "#C62828" : "#E5E7EB")
                  }
                />
                {errors.firstname && <p style={errStyle}>{errors.firstname}</p>}
              </div>
              <div>
                <label style={labelStyle}>Nom *</label>
                <input
                  data-testid="input-lastname"
                  type="text"
                  value={form.lastname}
                  onChange={(e) => set("lastname", e.target.value)}
                  placeholder="Dupont"
                  style={inputStyle("lastname")}
                  onFocus={(e) => (e.target.style.borderColor = "#12354D")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = errors.lastname ? "#C62828" : "#E5E7EB")
                  }
                />
                {errors.lastname && <p style={errStyle}>{errors.lastname}</p>}
              </div>
            </div>

            {/* Company */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Entreprise *</label>
              <div style={{ position: "relative" }}>
                <Building2
                  size={16}
                  color="#9CA3AF"
                  style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
                />
                <input
                  data-testid="input-company"
                  type="text"
                  value={form.company}
                  onChange={(e) => set("company", e.target.value)}
                  placeholder="Acme SAS"
                  style={iconInputStyle("company")}
                  onFocus={(e) => (e.target.style.borderColor = "#12354D")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = errors.company ? "#C62828" : "#E5E7EB")
                  }
                />
              </div>
              {errors.company && <p style={errStyle}>{errors.company}</p>}
            </div>

            {/* Postal code */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Code postal *</label>
              <div style={{ position: "relative" }}>
                <MapPin
                  size={16}
                  color="#9CA3AF"
                  style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
                />
                <input
                  data-testid="input-postal-code"
                  type="text"
                  value={form.postal_code}
                  onChange={(e) => set("postal_code", e.target.value)}
                  placeholder="87000"
                  maxLength={5}
                  style={iconInputStyle("postal_code")}
                  onFocus={(e) => (e.target.style.borderColor = "#12354D")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = errors.postal_code ? "#C62828" : "#E5E7EB")
                  }
                />
              </div>
              {errors.postal_code && <p style={errStyle}>{errors.postal_code}</p>}
            </div>

            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Email professionnel *</label>
              <div style={{ position: "relative" }}>
                <Mail
                  size={16}
                  color="#9CA3AF"
                  style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}
                />
                <input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="marie@acme.fr"
                  style={iconInputStyle("email")}
                  onFocus={(e) => (e.target.style.borderColor = "#12354D")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = errors.email ? "#C62828" : "#E5E7EB")
                  }
                />
              </div>
              {errors.email && <p style={errStyle}>{errors.email}</p>}
            </div>

            {/* RGPD */}
            <div
              style={{
                background: "#F9FAFB",
                borderRadius: 10,
                padding: "14px 14px",
                marginBottom: 16,
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
              }}
            >
              <input
                data-testid="input-rgpd"
                type="checkbox"
                id="rgpd"
                checked={form.rgpd_consent}
                onChange={(e) => set("rgpd_consent", e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, cursor: "pointer" }}
              />
              <label
                htmlFor="rgpd"
                style={{
                  fontSize: 12,
                  color: "#4A4A4A",
                  lineHeight: 1.5,
                  cursor: "pointer",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                J'accepte que <strong>Groupe LBS</strong> traite mes données personnelles
                dans le cadre de ce diagnostic et me contacte pour un suivi commercial.
              </label>
            </div>
            {errors.rgpd_consent && <p style={{ ...errStyle, marginBottom: 12 }}>{errors.rgpd_consent}</p>}

            {error && (
              <div
                data-testid="submit-error"
                style={{
                  padding: "12px 14px",
                  backgroundColor: "#FEF2F2",
                  border: "1px solid #FCA5A5",
                  borderRadius: 10,
                  color: "#C62828",
                  fontSize: 13,
                  marginBottom: 16,
                  fontFamily: "Inter, sans-serif",
                }}
              >
                {error}
              </div>
            )}

            <button
              data-testid="submit-button"
              type="submit"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? "Envoi en cours..." : "Recevoir mon rapport PDF"}
              {!submitting && <ArrowRight size={20} />}
            </button>
          </form>
        </div>

        {/* Security note */}
        <div
          className="flex items-center justify-center gap-2 mt-4"
          style={{ color: "rgba(255,255,255,0.3)" }}
        >
          <Shield size={11} />
          <p style={{ fontSize: 11, fontFamily: "Inter, sans-serif" }}>
            Données sécurisées — Conformité RGPD
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
